<template>
  <div class="tw-flex tw-flex-col tw-p-5 tw-gap-4">
    <div class="tw-flex tw-gap-4">
      <Stack
        gap="1"
        class="tw-flex-grow"
      >
        <Label>Name</Label>
        <TextInput
          v-model="name"
          placeholder="Enter portfolio name"
        />
      </Stack>
      <Stack
        gap="1"
        class="tw-w-48"
      >
        <Label>Portfolio Value ($)</Label>
        <TextInput
          :value="formattedPortfolioValue"
          placeholder="1,000,000"
          @input="parsePortfolioValue"
          @blur="onPortfolioValueBlur"
        />
      </Stack>
    </div>

    <Stack gap="2">
      <div class="tw-flex tw-items-center tw-justify-between">
        <Label>Holdings</Label>
        <Button
          variant="primary"
          icon-before="fas fa-plus"
          @click.prevent="addRow"
        >
          Add Holding
        </Button>
      </div>

      <Stack gap="1">
        <div
          v-for="(row, index) in holdingRows"
          :key="row._rowId"
          class="tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2.5 tw-border tw-border-gray-200"
        >
          <div class="tw-flex tw-items-center tw-gap-3">
            <Button
              v-if="holdingRows.length > 1"
              icon-only="fas fa-trash"
              variant="danger"
              size="2"
              @click.prevent="removeRow(index)"
            />

            <div class="tw-flex-grow tw-min-w-0">
              <div
                v-if="row.selected"
                class="tw-flex tw-items-center tw-gap-1.5"
              >
                <i
                  :class="getItemIcon(row.selected)"
                  class="tw-text-[#F1583D] tw-text-sm tw-flex-shrink-0"
                />
                <span class="tw-font-semibold tw-text-gray-900 tw-text-sm tw-truncate">
                  {{ row.selected.symbol || row.selected.name }}
                </span>
                <span class="tw-text-xs tw-text-gray-500 tw-truncate tw-hidden sm:tw-inline">
                  {{ row.selected.full_name || row.selected.description || 'Portfolio' }}
                </span>
              </div>

              <o-autocomplete
                v-else
                v-model="row.search"
                placeholder="Search symbols or portfolios"
                field="display"
                :data="row.searchResults"
                icon-pack="fa"
                icon="search"
                :icon-right="row.isLoading ? 'fa fa-spinner fa-spin' : ''"
                size="small"
                @input="debouncedRowSearch(index)"
                @select="(opt) => selectRowOption(index, opt)"
              >
                <template #default="{ option }">
                  <div class="tw-flex tw-items-center tw-gap-2 tw-py-1">
                    <i
                      :class="getItemIcon(option)"
                      class="tw-text-[#F1583D]"
                    />
                    <div>
                      <div class="tw-font-semibold tw-text-gray-900 tw-text-sm">
                        {{ option.ticker?.symbol || option.name }}
                      </div>
                      <div class="tw-text-xs tw-text-gray-600">
                        {{ option.ticker?.full_name || option.description || 'Portfolio' }}
                      </div>
                    </div>
                  </div>
                </template>
              </o-autocomplete>
            </div>

            <div class="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
              <o-input
                v-model="row.percentage"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                size="small"
                class="tw-w-16"
              />
              <span class="tw-text-gray-500 tw-text-sm">%</span>
            </div>
          </div>
        </div>
      </Stack>
    </Stack>

    <!-- Footer -->
    <div class="tw-border-t tw-border-gray-200 tw-pt-3">
      <div class="tw-flex tw-items-center tw-justify-between">
        <div class="tw-text-sm tw-font-medium tw-text-gray-700">
          Total:
          <span
            :class="isValidTotalPercent
              ? 'tw-text-green-600'
              : 'tw-text-red-600'"
          >{{ totalPercentage.toFixed(1) }}%</span>
          <span
            v-if="!isValidTotalPercent && selectedHoldings.length > 0"
            class="tw-text-xs tw-text-red-500 tw-ml-2"
          >Must equal 100%</span>
        </div>
        <StakeTooltip
          v-if="submitDisabledReason"
          :label="submitDisabledReason"
          position="left"
          tooltip-classes="tw-bg-slate-600 tw-text-white tw-whitespace-normal tw-break-words tw-w-44 tw-text-xs"
        >
          <span class="tw-inline-block">
            <Button
              variant="primary"
              :disabled="!canSubmit"
              @click="createPortfolio"
            >
              Create
            </Button>
          </span>
        </StakeTooltip>
        <Button
          v-else
          variant="primary"
          :disabled="!canSubmit"
          @click="createPortfolio"
        >
          Create
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import StakeTooltip from '@stakeui/StakeTooltip.vue';
import { Button, Label, Stack, TextInput } from '@stakeui';

export default {
  name: 'PortfolioCreator',
  components: {
    Button,
    Label,
    StakeTooltip,
    Stack,
    TextInput,
  },
  data() {
    return {
      name: '',
      portfolioValue: 1000000,
      portfolioValueDisplay: '1,000,000',
      holdingRows: [{
        _rowId: 1,
        search: '',
        searchResults: [],
        isLoading: false,
        selected: null,
        percentage: 0,
      }],
      nextRowId: 2,
    };
  },
  computed: {
    formattedPortfolioValue() {
      return this.portfolioValueDisplay;
    },
    hasUnselectedRowWithPercent() {
      return this.holdingRows.some(row => Number(row.percentage) > 0 && !row.selected);
    },
    selectedHoldings() {
      return this.holdingRows.filter(r => r.selected);
    },
    totalPercentage() {
      return this.holdingRows.reduce((total, row) => total + Number(row.percentage), 0);
    },
    isValidTotalPercent() {
      return Math.abs(this.totalPercentage - 100) < 0.001;
    },
    submitDisabledReason() {
      if (!this.name || !this.name.trim()) {
        return 'Enter a portfolio name';
      }
      if (this.hasUnselectedRowWithPercent) {
        return 'Select a ticker/portfolio for every row with a percentage';
      }
      if (!this.isValidTotalPercent) {
        return 'Must sum to 100%';
      }
      return '';
    },
    canSubmit() {
      return this.submitDisabledReason === '';
    },
  },
  methods: {
    getItemIcon(item) {
      if (item.ticker || item.symbol) {
        return 'fas fa-chart-bar'; // Symbol
      } else if (item.external_id) {
        return 'fas fa-chart-pie'; // Portfolio
      }
      return 'fas fa-question-circle';
    },
    createEmptyRow() {
      return {
        _rowId: 1,
        search: '',
        searchResults: [],
        isLoading: false,
        selected: null,
        percentage: 0,
      };
    },
    addRow() {
      this.holdingRows.push({
        _rowId: this.nextRowId++,
        search: '',
        searchResults: [],
        isLoading: false,
        selected: null,
        percentage: 0,
      });
    },
    removeRow(index) {
      this.holdingRows.splice(index, 1);
    },
    clearRowSelection(index) {
      this.holdingRows[index].selected = null;
      this.holdingRows[index].search = '';
      this.holdingRows[index].searchResults = [];
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('en-US');
    },
    parsePortfolioValue(val) {
      this.portfolioValueDisplay = val;
      const num = Number(String(val).replace(/[^0-9.]/g, ''));
      if (!isNaN(num) && num > 0) {
        this.portfolioValue = num;
      }
    },
    onPortfolioValueBlur() {
      this.portfolioValueDisplay = this.formatCurrency(this.portfolioValue);
    },
    async searchForRow(index) {
      const row = this.holdingRows[index];
      if (!row.search) {
        row.searchResults = [];
        return;
      }
      row.isLoading = true;
      try {
        const [symbolResults, portfolioResults] = await Promise.all([
          this.getSymbolSearchResults(row.search),
          this.getPortfolioSearchResults(row.search),
        ]);
        row.searchResults = [...symbolResults, ...portfolioResults];
      } catch (error) {
        console.error(error);
        row.searchResults = [];
      } finally {
        row.isLoading = false;
      }
    },
    debouncedRowSearch: (function() {
      const debouncers = {};
      return function(index) {
        if (!debouncers[index]) {
          debouncers[index] = debounce(idx => {
            this.searchForRow(idx);
          }, 200);
        }
        debouncers[index].call(this, index);
      };
    })(),
    selectRowOption(index, option) {
      if (!option) return;
      const row = this.holdingRows[index];
      if (option.ticker) {
        row.selected = { ...option.ticker };
      } else if (option.external_id) {
        row.selected = { ...option };
      }
      row.search = '';
      row.searchResults = [];
    },
    async createPortfolio() {
      const url = '/api/v1/portfolios/';

      // Build holdings array
      // Backend expects:
      // - percentage: decimal in range 0-1 (e.g., 0.5 for 50%)
      // - total_value: defaults to $1,000,000 if not provided
      // - Allocates dollars as: percentage * total_value
      const holdings = this.selectedHoldings.map(row => {
        const item = row.selected;
        if (item.ticker || item.symbol) {
          return {
            'identifier': item.id,
            'identifier_type': 'ticker_id',
            'percentage': row.percentage / 100,
          };
        } else if (item.external_id) {
          return {
            'identifier': item.external_id,
            'identifier_type': 'entity_id',
            'percentage': row.percentage / 100,
          };
        }
        return null;
      }).filter(Boolean);


      const req = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.$root.$data.csrfToken,
        },
        body: JSON.stringify({
          'name': this.name,
          'return_entity': true,
          'total_value': this.portfolioValue,
          'holdings': holdings,
        }),
      });
      if (req.ok) {
        const data = await req.json();
        console.log('Created portfolio', data);
        this.$emit('submit', data);
      } else {
        console.error('Error creating portfolio');
      }
    },
    async getSymbolSearchResults(query) {
      try {
        const response = await fetch('/api/v1/tickers/?search=$' + query);
        const data = await response.json();
        return (data.tickers || []).map(result => ({
          ...result,
          display: `${result.ticker.symbol} - ${result.ticker.full_name}`,
          type: 'symbol',
        }));
      } catch (error) {
        console.error('Error fetching symbols:', error);
        return [];
      }
    },
    async getPortfolioSearchResults(query) {
      try {
        const params = { search: query, return_entities: true };
        const response = await fetch(`/api/v1/portfolios/?${new URLSearchParams(params)}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        return (data.portfolios || []).map(result => ({
          ...result,
          display: `${result.name} - ${result.description || 'Portfolio'}`,
          type: 'portfolio',
        }));
      } catch (error) {
        console.error('Error fetching portfolios:', error);
        return [];
      }
    },
  },
};
</script>

<style scoped>
.inline-input {
  display: inline-block;
}
</style>
